import type { CityAbbreviationMatch, CityStateRow } from './types';

/** Groups a flat {@link CityAbbreviationMatch} list by the originating row's `id`, preserving match order within each group. */
export const groupMatchesById = (matches: CityAbbreviationMatch[]): Record<string, CityAbbreviationMatch[]> =>
    matches.reduce((acc, m) => {
        (acc[m.id] = acc[m.id] || []).push(m);
        return acc;
    }, {} as Record<string, CityAbbreviationMatch[]>);

/** Merges `extraCity`/`extraAbbr` into an existing `;;`-delimited CityAbbreviation string, skipping values already present. */
export const mergeCityAbbreviation = (base: string | undefined, extraCity?: string, extraAbbr?: string): string => {
    const parts = (base || '').split(';;').filter(Boolean);
    if (extraCity && !parts.includes(extraCity)) parts.push(extraCity);
    (extraAbbr || '').split(';;').filter(Boolean).forEach((p) => {
        if (!parts.includes(p)) parts.push(p);
    });
    return `;;${parts.join(';;')};;`;
};

/** Status choices offered by the bulk Apply Status action, applied to every currently loaded row. */
export const STATUS_CHOICES = [{ id: '0', name: '0' }, { id: '1', name: '1' }, { id: '2', name: '2' }];

/**
 * A row is invalid when it cannot safely be inserted/updated into `venue_city_states`:
 * `stateId` isn't exactly 2 uppercase letters, or `stateName`/`country` is missing.
 * or length of `city` <= 2
 */
export const isRowInvalid = (row: CityStateRow): boolean =>
    !/^[A-Z]{2}$/.test(row.stateId || '') || !row.stateName || !row.country || (row.city || '').length <= 2;

/** Stable identity key for a row, matching the grid's `getRowId`; used to persist selection/edits across filter changes. */
export const getRowKey = (row: Pick<CityStateRow, 'marketType' | 'city' | 'stateId'>): string =>
    `${row.marketType}|${row.city}|${row.stateId}`;

/**
 * Gets the city abbreviation for a given row and match.
 * @param row The city/state row.
 * @param match The city abbreviation match.
 * @returns The city abbreviation.
 */
export const getCityAbbreviation = (row: CityStateRow, match: CityAbbreviationMatch): string => {
    const abbreviations = (match.cityAbbreviation || '')
        .split(';;')
        .filter(Boolean);
    if (row.city && !abbreviations.includes(row.city)) {
        abbreviations.push(row.city);
    }
    return `;;${abbreviations.join(';;')};;`;
};
